
import { Component, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { Validate } from '@/types';
import { CateringBotView, CateringPackage, CateringRecommendationRequest } from '@/types/cateringBot';
import Modal from '@/components/shared/Modal.vue';
import CateringBotPackageCard from '@/components/menu/cateringBot/CateringBotPackageCard.vue';
import LoadingSpinner from '@/components/shared/LoadingSpinner.vue';
import InfoBanner from '@/components/shared/InfoBanner.vue';

const namespace: string = 'cateringBot';

@Component<CateringBotModal>({
	components: {
		Modal,
		CateringBotPackageCard,
		LoadingSpinner,
		InfoBanner,
		ValidationObserver,
		ValidationProvider
	}
})
export default class CateringBotModal extends Vue {
	@Getter('lastInput', { namespace }) private lastInput!: CateringRecommendationRequest | null;
	@Getter('packagesToRender', { namespace }) private packagesToRender!: CateringPackage[];
	@Getter('creditsRemaining', { namespace: 'rallyIntelligence' }) private creditsRemaining!: number;
	@Getter('creditsExhausted', { namespace: 'rallyIntelligence' }) private creditsExhausted!: boolean;
	@Getter('getDiets', { namespace: 'filters' }) private filterDiets!: FilterTag[];
	@Getter('getAllergens', { namespace: 'filters' }) private filterAllergens!: FilterTag[];
	@Action('getRecommendations', { namespace }) private getRecommendations!: (input: CateringRecommendationRequest) => Promise<void>;

	private view: CateringBotView = 'input';
	private loading: boolean = false;
	private errorMessage: string = '';

	private form: { guests: number; budget: number; special: string } = {
		guests: 10,
		budget: 500,
		special: ''
	};

	private dietOptions: FilterTag[] = [];
	private allergenOptions: FilterTag[] = [];

	$refs!: {
		observer: Validate;
		container: HTMLDivElement
	};

	private created(): void {
		this.dietOptions = this.filterDiets.map(opt => ({ ...opt, selected: false }));
		this.allergenOptions = this.filterAllergens.map(opt => ({ ...opt, selected: false }));
		this.view = this.packagesToRender.length > 0 ? 'results' : 'input';
	}

	private toggleDiet(index: number): void {
		this.dietOptions[index].selected = !this.dietOptions[index].selected;
	}

	private toggleAllergen(index: number): void {
		this.allergenOptions[index].selected = !this.allergenOptions[index].selected;
	}

	private scrollToTop(): void {
		this.$nextTick(() => {
			this.$refs.container?.scrollTo({ top: 0, behavior: 'smooth' });
		});
	}

	private goBack(): void {
		if (this.creditsExhausted) {
			this.$emit('close');
			return;
		}

		this.view = 'input';
		this.errorMessage = '';
	}

	private async onSubmit(): Promise<void> {
		const isValid = await this.$refs.observer.validate();

		if (!isValid) {
			return;
		}

		const selectedDiets = this.dietOptions.filter(opt => opt.selected).map(opt => opt.value);
		const selectedAllergens = this.allergenOptions.filter(opt => opt.selected).map(opt => opt.value);

		try {
			this.loading = true;
			this.errorMessage = '';
			await this.getRecommendations({
				guests: this.form.guests,
				budget: this.form.budget,
				dietary_preferences: selectedDiets.length ? selectedDiets : undefined,
				allergens: selectedAllergens.length ? selectedAllergens : undefined,
				special_requests: this.form.special.trim() || undefined
			});
			this.view = this.packagesToRender.length > 0 ? 'results' : 'empty';
		}
		catch (err) {
			const code = (err as any)?.response?.data?.code;
			if (code === 'rally_token_limit_exceeded') {
				this.errorMessage = this.$t('rallyIntelligence.shared.errors.token_limit');
			}
			else if (code === 'rally_content_filter') {
				this.errorMessage = this.$t('rallyIntelligence.shared.errors.content_filter');
			}
			else {
				this.errorMessage = this.$t('rallyIntelligence.shared.errors.unavailable');
			}
			this.view = 'error';
		}
		finally {
			this.loading = false;
		}
	}
}
